/**
 * features/parent/GuardianContactPrompt.tsx
 * ------------------------------------------------------------------
 * Parent-portal SOFT GATE (phase 4 revised): when a signed-in parent's own
 * guardian-contact fields are missing on a child, prompt them to submit the
 * values. Dismissible (never blocks the portal) and reappears next session.
 *
 * Parents do NOT write live — save SUBMITS a pending request via the
 * submitGuardianUpdate CF; an admin reviews + approves before it applies.
 * NAG SUPPRESSION: a field counts as "handled" if it's already FILLED OR has
 * a PENDING request, so a parent who submitted isn't re-nagged while it waits.
 * ------------------------------------------------------------------
 */
import { useMemo, useState, useEffect } from 'react';
import { UserCog, Phone, MessageCircle, Mail, X, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import type { Student, Guardian } from '@/types';

const FIELDS: { key: 'phone' | 'whatsapp' | 'email'; label: string; icon: typeof Phone; type: string }[] = [
  { key: 'phone', label: 'Phone', icon: Phone, type: 'tel' },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, type: 'tel' },
  { key: 'email', label: 'Email', icon: Mail, type: 'email' },
];

export default function GuardianContactPrompt() {
  const { students } = useData();
  const { userData } = useAuth();

  const guardianId = userData?.parentAccount?.guardianId;
  const myEmail = (userData?.email || '').toLowerCase();
  const linked = userData?.parentAccount?.studentIds ?? [];

  const [dismissed, setDismissed] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // studentId -> set of fields that already have a PENDING request (nag suppression).
  const [pendingFields, setPendingFields] = useState<Record<string, Set<string>>>({});

  // Load the parent's own pending requests (scoped by parentUid — rules-allowed).
  useEffect(() => {
    const uid = userData?.uid;
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'reportGuardianUpdates'), where('parentUid', '==', uid)));
        const map: Record<string, Set<string>> = {};
        snap.forEach((d) => {
          const r = d.data() as { studentId: string; status: string; proposedFields?: Record<string, string> };
          if (r.status !== 'pending') return;
          const set = map[r.studentId] ?? new Set<string>();
          Object.keys(r.proposedFields ?? {}).forEach((f) => set.add(f));
          map[r.studentId] = set;
        });
        if (!cancelled) setPendingFields(map);
      } catch { /* portal still works if this read fails */ }
    })();
    return () => { cancelled = true; };
  }, [userData?.uid]);

  const myGuardian = (s: Student): Guardian | undefined => {
    const gs = s.admissions?.guardians ?? [];
    const byId = guardianId ? gs.find((g) => g.id === guardianId) : undefined;
    if (byId) return byId;
    return myEmail ? gs.find((g) => (g.email || '').toLowerCase() === myEmail) : undefined;
  };
  // Missing = blank on record AND no pending request for that field.
  const missingFor = (s: Student) => {
    const g = myGuardian(s);
    const pend = pendingFields[s.id] ?? new Set<string>();
    return FIELDS.filter((f) => !((g?.[f.key] || '').toString().trim()) && !pend.has(f.key)).map((f) => f.key);
  };

  const children = useMemo(
    () =>
      students
        .filter((s) => (linked.length ? linked.includes(s.id) : true))
        .map((s) => ({ student: s, missing: missingFor(s) }))
        .filter((c) => c.missing.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [students, guardianId, myEmail, linked, pendingFields],
  );

  if (dismissed || children.length === 0) return null;

  async function save(studentId: string, missing: string[]) {
    const values = drafts[studentId] ?? {};
    const payload: Record<string, string> = { studentId };
    const submitted: string[] = [];
    for (const k of missing) {
      const v = (values[k] || '').trim();
      if (v) { payload[k] = v; submitted.push(k); }
    }
    if (submitted.length === 0) { setErr('Enter at least one contact detail to submit.'); return; }
    setSavingId(studentId);
    setErr(null);
    try {
      const fn = httpsCallable(functions, 'submitGuardianUpdate');
      await fn(payload);
      // Suppress the nag for the submitted fields (now pending review).
      setPendingFields((prev) => {
        const set = new Set(prev[studentId] ?? []);
        submitted.forEach((f) => set.add(f));
        return { ...prev, [studentId]: set };
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="rounded-2xl border-2 border-[#d4a92c]/60 bg-amber-50 dark:bg-amber-900/15 dark:border-amber-700/60 p-5 mb-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <UserCog size={18} className="text-[#2d5295]" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Complete your contact details</h3>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          title="Dismiss (we'll ask again next time)"
        >
          <X size={18} />
        </button>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
        Help the school reach you. Add your contact details below — the school
        will review and apply them.
      </p>

      {err && (
        <div className="mt-3 flex items-center gap-2 text-sm rounded-lg px-3 py-2 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">
          <AlertTriangle size={15} /> {err}
        </div>
      )}

      <div className="mt-4 space-y-3">
        {children.map(({ student, missing }) => (
          <div key={student.id} className="rounded-xl border border-amber-200 dark:border-amber-800 bg-white dark:bg-gray-800 p-3">
            <div className="font-medium text-gray-900 dark:text-white mb-2">{student.fullName}</div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              {FIELDS.filter((f) => missing.includes(f.key)).map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.key} className="flex-1">
                    <label className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      <Icon size={12} /> {f.label}
                    </label>
                    <input
                      type={f.type}
                      value={drafts[student.id]?.[f.key] ?? ''}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [student.id]: { ...(d[student.id] ?? {}), [f.key]: e.target.value } }))
                      }
                      placeholder={f.key === 'email' ? 'you@example.com' : '+267 …'}
                      className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                    />
                  </div>
                );
              })}
              <button
                onClick={() => save(student.id, missing)}
                disabled={savingId === student.id}
                className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-lg bg-[#2d5295] text-white text-sm font-semibold hover:bg-[#1b3a6b] disabled:opacity-40"
              >
                {savingId === student.id ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                Submit
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-3">Submitted details are reviewed by the school before they appear on the record.</p>
    </div>
  );
}
